import { Pipe, PipeTransform } from '@angular/core';
import { getDocumentIcon } from '../services/functions-utils.service';

/**
 * Pipe that returns the appropriate preview URL or icon data for a file
 * Usage: {{ fileUrl | filePreview:fileName }}
 */
@Pipe({
  name: 'filePreview',
  standalone: true,
})
export class FilePreviewPipe implements PipeTransform {
  private imageCache = new Map<string, { exists: boolean; url?: string }>();

  /**
   * Transforms a file URL into preview data
   * @param fileUrl The URL of the file to preview
   * @param fileName The name of the file (used for icon fallback)
   * @returns Object containing either the preview URL or icon data
   */
  async transform(
    fileUrl: string | null | undefined,
    fileName?: string,
    fileType?: string
  ): Promise<{
    type: 'url' | 'icon';
    url?: string;
    icon?: string;
    color?: string;
  }> {
    // If no URL provided, return icon immediately
    const iconData = getDocumentIcon(fileName, fileType);
    if (!fileUrl) {
      return {
        type: 'icon',
        icon: iconData.icon,
        color: iconData.color,
      };
    }

    // Check cache first
    if (!fileUrl.startsWith('http')) {
      let apiUrls = JSON.parse(window.sessionStorage.getItem('APIURLS') ?? '[]');
      let url = apiUrls.find((x: any) => x.id == 'LOCALURL')?.value;
      if (url) {
        url = url?.replace('/api', '');
        fileUrl = url.replace(/\/$/, '') + '/' + fileUrl.replace(/^\//, '');
      }
    }
    if (this.imageCache.has(fileUrl)) {
      const cached = this.imageCache.get(fileUrl)!;
      if (cached.exists) {
        return { type: 'url', url: cached.url };
      } else {
        return {
          type: 'icon',
          icon: iconData.icon,
          color: iconData.color,
        };
      }
    }

    // Check if the URL exists by attempting to load it
    try {
      const exists = await this.checkImageExists(fileUrl, fileType);

      if (exists) {
        this.imageCache.set(fileUrl, { exists: true, url: fileUrl });
        return { type: 'url', url: fileUrl };
      } else {
        this.imageCache.set(fileUrl, { exists: false });
        return {
          type: 'icon',
          icon: iconData.icon,
          color: iconData.color,
        };
      }
    } catch (error) {
      // On error, fall back to icon
      this.imageCache.set(fileUrl, { exists: false });
      return {
        type: 'icon',
        icon: iconData.icon,
        color: iconData.color,
      };
    }
  }

  /**
   * Checks if an image URL exists and is loadable
   * @param url The URL to check
   * @returns Promise that resolves to true if image exists
   */
  private checkImageExists(url: string, fileType?: string): Promise<boolean> {
    if (['xls', 'xlsx', 'msg', 'eml', 'zip', 'txt', 'xml', 'json'].includes(fileType?.replace('.', '') ?? '')) {
      return Promise.resolve(false);
    }
    return new Promise((resolve) => {
      const img = new Image();

      img.onload = () => {
        resolve(true);
      };

      img.onerror = () => {
        resolve(false);
      };

      // Set timeout to avoid hanging
      setTimeout(() => {
        resolve(false);
      }, 5000);

      img.src = url;
    });
  }
}
